import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { canPublish, useEditorialMe } from './me'
import { listLive, messagePreview, openEdit, putLiveBackUp, takeDown, type LiveCopy, type LiveItem, type TakeDownReason } from './api'
import { useEditorialTitle } from './titleContext'
import { pieceHref, versionHref } from './links'
import { useVersionsOn } from './useVersionsOn'
import { describeError } from '../lib/describeError'
import { useDebounced } from '../lib/useDebounced'
import { etDate, etTime } from './labels'
import {
  CUSTOM_MAX, cameFromLabel, PAGE_SIZE, REASON_LABEL, REASON_OPTIONS, canTakeDown, copiesLabel, copyLabel, hasTwin, parseWhere, supportsMembership,
  putBackChoices, putBackErrorText, putBackIsStale, tabLabels, takeDownChoices, takeDownLabel,
} from './live'
import { countingSince, liveReadersLine, liveGainHeader, pct, pctOrDash } from './readers'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'
const field = `min-h-11 w-full rounded-lg border border-ink-400 bg-ink-800 px-3 font-ui text-[14px] text-paper placeholder:text-paper-faint ${focus}`
const btn = `inline-flex min-h-10 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-4 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`
const btnSmall = `inline-flex min-h-9 items-center justify-center whitespace-nowrap rounded-lg border border-ink-400 px-3 font-ui text-[14px] font-semibold text-paper hover:bg-ink-600 disabled:opacity-50 ${focus}`
const btnDanger = `inline-flex min-h-10 items-center justify-center rounded-lg border border-radar-red bg-radar-red px-4 font-ui text-[14px] font-semibold text-paper hover:opacity-90 disabled:opacity-50 ${focus}`
const th = 'whitespace-nowrap border-b border-ink-500 px-4 py-3 text-left font-ui text-[12px] font-bold uppercase tracking-[0.06em] text-paper-faint'
const td = 'border-b border-ink-600 px-4 py-3.5 align-middle'

function TakeDownPanel({ item, onClose, onDone }: { item: LiveItem; onClose: () => void; onDone: () => void }) {
  const { section, publicationSlug } = useEditorialTitle()
  const twin = hasTwin(item)
  const choices = takeDownChoices(item)
  const [copies, setCopies] = useState<LiveCopy[]>(choices)
  const [reason, setReason] = useState<TakeDownReason>('unavailable')
  const [customText, setCustomText] = useState('')
  const [note, setNote] = useState('')
  const [error, setError] = useState<string | null>(null)
  const form = { copies, reason, customText, hasTwin: twin }
  const ok = canTakeDown(form)
  const debounced = useDebounced({ reason, customText }, 300)
  const preview = useQuery({
    queryKey: ['editorial-live-preview', item.article_id, debounced.reason, debounced.reason === 'custom' ? debounced.customText : ''],
    queryFn: () => messagePreview(item.article_id, debounced.reason, debounced.customText),
    enabled: debounced.reason !== 'custom' || (debounced.customText.trim().length > 0 && debounced.customText.length <= CUSTOM_MAX),
    retry: false,
  })
  const go = useMutation({
    mutationFn: () => takeDown(item.article_id, {
      copies, reason, custom_text: reason === 'custom' ? customText.trim() : undefined, note: note.trim() || undefined,
    }),
    onSuccess: onDone,
    onError: (e) => setError(describeError(e)),
  })
  const toggle = (c: LiveCopy) => setCopies((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : choices.filter((x) => x === c || cur.includes(x))))
  const archiveClash = reason === 'member_archive' && twin && !(copies.length === 1 && copies[0] === 'free')

  return (
    <>
      <div className="fixed inset-0 z-30 bg-ink-900/70 lg:hidden" onClick={onClose} aria-hidden="true" />
      <aside role="dialog" aria-modal="true" aria-labelledby="td-title"
        className="fixed inset-x-3 bottom-3 top-3 z-40 flex flex-col gap-4 overflow-y-auto rounded-2xl border border-ink-500 bg-ink-700 p-5 font-ui text-paper shadow-xl lg:static lg:inset-auto lg:z-auto lg:max-w-[440px] lg:flex-[1_1_340px] lg:self-start">
        <span className="label">Take down</span>
        <h2 id="td-title" className="m-0 font-serif text-[23px] font-medium leading-tight">{item.title}</h2>

        <fieldset className="m-0 flex flex-col gap-1 border-0 p-0">
          <legend className="mb-1 text-[14px] font-semibold">Take it down from</legend>
          {choices.map((c) => (
            <label key={c} className="flex cursor-pointer items-start gap-2.5 py-2">
              <input type="checkbox" checked={copies.includes(c)} onChange={() => toggle(c)} className="mt-0.5 size-[18px] flex-none accent-radar-red" />
              <span className="text-[14px]">{copyLabel(c, section.label)}</span>
            </label>
          ))}
        </fieldset>

        <fieldset className="m-0 flex flex-col border-0 p-0">
          <legend className="mb-1 text-[14px] font-semibold">What should its page tell readers?</legend>
          {REASON_OPTIONS.map((o) => {
            const disabled = o.key === 'member_archive' && !twin
            return (
              <label key={o.key} className={`flex items-start gap-2.5 py-2 ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}>
                <input type="radio" name="why" checked={reason === o.key} disabled={disabled} onChange={() => setReason(o.key)} className="mt-0.5 size-[18px] flex-none accent-radar-red" />
                <span className="text-[14px]">
                  <strong className="font-semibold">{o.label}</strong>
                  {disabled ? <span className="block text-[13px] text-paper-dim">No member copy of this piece exists.</span>
                    : o.hint && <span className="block text-[13px] text-paper-dim">{o.hint}</span>}
                </span>
              </label>
            )
          })}
          {reason === 'custom' && (
            <div className="mt-1 flex flex-col gap-1">
              <label className="sr-only" htmlFor="td-custom">Your own words</label>
              <textarea id="td-custom" value={customText} onChange={(e) => setCustomText(e.target.value)} rows={3} className={`${field} py-2`} />
              <span className={`text-right text-[12px] ${customText.length > CUSTOM_MAX ? 'text-radar-red' : 'text-paper-faint'}`}>{customText.length} / {CUSTOM_MAX}</span>
            </div>
          )}
          {archiveClash && <p className="m-0 mt-1 text-[13px] text-amber-glow">The member archive message is for the free copy only. Take down just the free copy to use it.</p>}
        </fieldset>

        <div className="rounded-lg bg-ink-800 px-3.5 py-3 text-[13px] text-paper-dim" aria-live="polite">
          <span className="label mb-1.5 block">Readers will see</span>
          {preview.isPending && preview.fetchStatus === 'idle' && <span>Write the words readers should see.</span>}
          {preview.isFetching && !preview.data && <span>Loading…</span>}
          {preview.isError && <span className="text-radar-red">{describeError(preview.error)}</span>}
          {preview.data && !preview.isError && <span className="font-serif text-[15px] text-paper">{preview.data.message}</span>}
        </div>

        <label className="flex flex-col gap-1.5 text-[14px] font-semibold">
          Note for the team (optional, not shown to readers)
          <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. moved behind the paywall" className={`${field} font-normal`} />
        </label>

        {error && <p role="alert" className="m-0 text-[13px] text-radar-red">{error}</p>}
        <div className="flex flex-wrap justify-end gap-2.5">
          <button type="button" className={btn} onClick={onClose}>Cancel</button>
          <button type="button" className={btnDanger} disabled={!ok || go.isPending} onClick={() => { setError(null); go.mutate() }}>
            {go.isPending ? 'Taking down…' : takeDownLabel(copies, section.label)}
          </button>
        </div>
        <span className="sr-only">{publicationSlug}</span>
      </aside>
    </>
  )
}

function Row({ item, selected, onTakeDown, onPutBack, onEdit, editing, busy, canAct, canEdit, mixed }: {
  item: LiveItem; selected: boolean; onTakeDown: () => void; onPutBack: () => void; busy: boolean
  /** Opens the article's edit draft (native titles only). */
  onEdit: () => void
  editing: boolean
  /** The title publishes on the new site (LiveList.can_edit): live free and members rows offer Edit. */
  canEdit: boolean
  /** False for a writer: Live is read-only for them. */
  canAct: boolean
  /** True when the list mixes desk and raw rows: each cell then names its unit. */
  mixed: boolean
}) {
  const { section, publicationSlug } = useEditorialTitle()
  const versionsOn = useVersionsOn(publicationSlug)
  const url = item.url
  const down = item.taken_down
  return (
    <tr className={selected ? 'bg-radar-red/10' : undefined}>
      <td className={`${td} max-w-[380px]`}>
        <span className="block font-serif text-[17px] font-medium leading-tight text-paper">{item.title}</span>
        <span className="mt-0.5 block text-[12px] text-paper-faint md:hidden">{liveReadersLine(item.readers)}</span>
        {item.twin && <span className="block text-[12px] text-radar-violet">Summary of a Member exclusive</span>}
        {item.draft_id !== null && (
          <Link to={pieceHref(section.path, item.draft_id)} className={`font-ui text-[13px] font-semibold text-amber-glow underline underline-offset-2 ${focus}`}>Open the piece</Link>
        )}
        {down && (
          <span className="mt-1 block text-[12px] text-paper-dim">
            {down.reason ? REASON_LABEL[down.reason] ?? down.reason : 'Taken down'}
            {down.by && ` · ${down.by}`}{down.at && ` · ${etDate(down.at)}, ${etTime(down.at)}`}
          </span>
        )}
      </td>
      <td className={td}>
        <span className={`inline-flex h-6 items-center rounded-full px-2.5 font-ui text-[12px] font-semibold ${item.came_from !== 'wordpress' ? 'bg-amber-glow/20 text-amber-deep' : 'bg-ink-600 text-paper-dim'}`}>
          {cameFromLabel(item.came_from)}
        </span>
      </td>
      <td className={`${td} whitespace-nowrap font-ui text-[14px] text-paper-dim`}>
        {item.live_since ? `${etDate(item.live_since)}, ${etTime(item.live_since)}` : '—'}
      </td>
      <td className={`${td} hidden whitespace-nowrap font-ui text-[14px] text-paper-dim md:table-cell`}>{item.readers.people}</td>
      <td className={`${td} hidden whitespace-nowrap font-ui text-[14px] text-paper-dim md:table-cell`}>{pctOrDash(item.readers.finished_pct)}</td>
      <td className={`${td} hidden whitespace-nowrap font-ui text-[14px] text-paper-dim md:table-cell`}>{pct(item.readers.members_pct)}</td>
      <td className={`${td} hidden whitespace-nowrap font-ui text-[14px] text-paper-dim md:table-cell`}>{item.readers.source === 'desk' ? (item.readers.members_gained ?? 0) : item.readers.signups_after}
        {mixed && <span className="ml-1 text-[12px] text-paper-faint">{item.readers.source === 'desk' ? 'gained' : 'sign-ups'}</span>}
      </td>
      <td className={`${td} whitespace-nowrap text-right`}>
        {canAct && canEdit && !down && (item.where === 'free' || item.where === 'members') && (
          <button type="button" className={`${btnSmall} mr-3.5`} disabled={editing} onClick={onEdit} aria-label={`Edit: ${item.title}`}>
            {editing ? 'Opening…' : 'Edit'}
          </button>
        )}
        {versionsOn && !down && (
          <Link to={versionHref(section.path, item.draft_id !== null ? { draftId: item.draft_id } : { articleId: item.article_id })}
                state={{ title: item.title }} className={`mr-3.5 font-ui text-[14px] font-semibold text-amber-glow underline underline-offset-2 ${focus}`}
                aria-label={`Make a version: ${item.title}`}>Make a version</Link>
        )}
        {url && !down && (
          <a href={url} target="_blank" rel="noreferrer" aria-label={`View: ${item.title}`} className={`mr-3.5 font-ui text-[14px] font-semibold text-amber-glow underline underline-offset-2 ${focus}`}>View</a>
        )}
        {canAct && (down || item.down.length > 0) && (
          <button type="button" className={btnSmall} disabled={busy} onClick={onPutBack} aria-label={`Put back up: ${item.title}`}>Put back up…</button>
        )}
        {canAct && !down && <button type="button" className={`${btnSmall} ml-2`} onClick={onTakeDown} aria-label={`Take down: ${item.title}`}>Take down…</button>}
      </td>
    </tr>
  )
}

export function LivePage() {
  const { section, publicationSlug } = useEditorialTitle()
  const me = useEditorialMe()
  const membership = supportsMembership(publicationSlug)
  const [params, setParams] = useSearchParams()
  const where = parseWhere(params.get('where'), membership)
  const [text, setText] = useState('')
  const q = useDebounced(text, 300).trim()
  const [target, setTarget] = useState<LiveItem | null>(null)
  const [message, setMessage] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [confirming, setConfirming] = useState<LiveItem | null>(null)
  const qc = useQueryClient()
  const navigate = useNavigate()

  const list = useInfiniteQuery({
    queryKey: ['editorial-live', publicationSlug, where, q],
    queryFn: ({ pageParam }) => listLive(publicationSlug, { where, q, limit: PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((n, p) => n + p.items.length, 0)
      return last.items.length > 0 && loaded < last.total ? loaded : undefined
    },
  })
  const rows = list.data?.pages.flatMap((p) => p.items) ?? []
  const counts = list.data?.pages[0]?.counts
  const since = countingSince(list.data?.pages[0]?.tracking_since ?? null)
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['editorial-live', publicationSlug] }) }

  const [backCopies, setBackCopies] = useState<LiveCopy[]>([])
  const back = useMutation({
    mutationFn: ({ item, copies }: { item: LiveItem; copies: LiveCopy[] }) => putLiveBackUp(item.article_id, copies),
    onSuccess: (_r, { item }) => { setConfirming(null); setError(null); setMessage(`“${item.title}” is back up.`); refresh() },
    onError: (e) => { setConfirming(null); setError(putBackErrorText(e)); if (putBackIsStale(e)) refresh() },
  })

  const canEdit = !!list.data?.pages[0]?.can_edit
  const edit = useMutation({
    mutationFn: (item: LiveItem) => openEdit(item.article_id),
    onSuccess: (r) => navigate(pieceHref(section.path, r.draft_id)),
    onError: (e) => setError(describeError(e)),
  })

  const select = (key: string) => {
    setTarget(null)
    setConfirming(null)
    const next = new URLSearchParams(params)
    if (key === 'free') next.delete('where'); else next.set('where', key)
    setParams(next, { replace: true })
  }

  return (
    <div className="flex flex-col gap-5 font-ui text-paper">
      <header className="flex flex-col gap-1.5">
        <span className="label">{section.label} · Live</span>
        <h1 className="m-0 font-serif text-[40px] font-medium leading-[1.05] tracking-tight text-paper">Everything readers can see</h1>
        <p className="m-0 max-w-[720px] text-[14px] text-paper-dim">
          Pieces written here and posts filed directly in WordPress, in one list. Taking a piece down hides it from readers; nothing is deleted, and you can put it back.
        </p>
      </header>

      <div className="flex flex-wrap items-start gap-5">
        <div className="flex min-w-0 flex-[3_1_560px] flex-col gap-3">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div role="tablist" aria-label="Where" className="flex flex-wrap gap-1 rounded-[10px] bg-ink-800 p-1">
              {tabLabels(section.label, membership).map((t) => {
                const on = t.key === where
                const n = counts?.[t.key]
                return (
                  <button key={t.key} type="button" role="tab" aria-selected={on} onClick={() => select(t.key)}
                    className={`min-h-9 rounded-[7px] px-3 font-ui text-[13px] font-semibold ${focus} ${on ? 'bg-ink-600 text-paper' : 'text-paper-dim hover:text-paper'}`}>
                    {t.label}{n !== undefined && <span className="ml-1.5 font-medium opacity-70">{n.toLocaleString('en-US')}</span>}
                  </button>
                )
              })}
            </div>
            <label className="flex">
              <span className="sr-only">Search live pieces</span>
              <input type="search" value={text} onChange={(e) => setText(e.target.value)} placeholder="Search titles" className={`${field} w-60`} />
            </label>
          </div>

          {message && <p role="status" className="m-0 text-[14px] text-radar-green">{message}</p>}
          {error && <p role="alert" className="m-0 text-[14px] text-radar-red">{error}</p>}
          {confirming && (
            <div role="alertdialog" aria-label="Put back up" className="flex flex-wrap items-center gap-3 rounded-xl border border-ink-500 bg-ink-700 px-4 py-3 text-[14px]">
              {putBackChoices(confirming).length === 1
                ? <span>Put this back up on {copiesLabel(backCopies, section.label)}?</span>
                : (
                  <fieldset className="m-0 flex flex-wrap items-center gap-3 border-0 p-0">
                    <legend className="sr-only">Copies to put back up</legend>
                    <span>Put back up:</span>
                    {putBackChoices(confirming).map((c) => (
                      <label key={c} className="flex items-center gap-1.5">
                        <input type="checkbox" checked={backCopies.includes(c)} className="size-[18px] accent-amber-glow"
                          onChange={() => setBackCopies((cur) => (cur.includes(c) ? cur.filter((x) => x !== c) : [...cur, c]))} />
                        {copyLabel(c, section.label)}
                      </label>
                    ))}
                  </fieldset>
                )}
              <button type="button" className={btn} onClick={() => setConfirming(null)}>Cancel</button>
              <button type="button" className={btn} disabled={back.isPending || backCopies.length === 0}
                onClick={() => back.mutate({ item: confirming, copies: backCopies })}>{back.isPending ? 'Putting back…' : 'Put back up'}</button>
            </div>
          )}

          {list.isPending && <p role="status" className="m-0 text-[14px] text-paper-dim">Loading…</p>}
          {list.isError && <p role="alert" className="m-0 text-[14px] text-radar-red">Could not load. {describeError(list.error)}</p>}
          {list.data && (
            <div className="overflow-x-auto rounded-xl border border-ink-500 bg-ink-700">
              <table className="w-full min-w-[640px] border-collapse">
                <thead>
                  <tr>
                    <th scope="col" className={th}>Piece</th>
                    <th scope="col" className={th}>Came from</th>
                    <th scope="col" className={th}>Live since</th>
                    <th scope="col" className={`${th} hidden md:table-cell`}>People (30 d)</th>
                    <th scope="col" className={`${th} hidden md:table-cell`}>Finished</th>
                    <th scope="col" className={`${th} hidden md:table-cell`}>Members</th>
                    <th scope="col" className={`${th} hidden md:table-cell`}>{liveGainHeader(rows)}</th>
                    <th scope="col" className={th}><span className="sr-only">Actions</span></th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <Row key={r.article_id} item={r} selected={target?.article_id === r.article_id} busy={back.isPending} canAct={canPublish(me.data)}
                      canEdit={canEdit} editing={edit.isPending && edit.variables?.article_id === r.article_id}
                      onEdit={() => { setMessage(null); setError(null); edit.mutate(r) }} mixed={liveGainHeader(rows) === 'Sign-ups / members gained'}
                      onTakeDown={() => { setMessage(null); setTarget(r) }}
                      onPutBack={() => { setMessage(null); setError(null); setBackCopies(putBackChoices(r)); setConfirming(r) }} />
                  ))}
                  {rows.length === 0 && <tr><td colSpan={8} className={`${td} text-[14px] text-paper-dim`}>Nothing here.</td></tr>}
                </tbody>
              </table>
            </div>
          )}
          {since && <p className="m-0 text-[12px] text-paper-faint">Reader numbers cover the last 30 days. {since}.</p>}
          {list.hasNextPage && (
            <div className="flex justify-center">
              <button type="button" className={btn} disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
                {list.isFetchingNextPage ? 'Loading…' : 'Show more'}
              </button>
            </div>
          )}
        </div>

        {target && (
          <TakeDownPanel key={target.article_id} item={target} onClose={() => setTarget(null)}
            onDone={() => { setMessage(`“${target.title}” is taken down.`); setTarget(null); refresh() }} />
        )}
      </div>
    </div>
  )
}
